import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Users, Clock, Sparkles, ArrowRight, Radio, MessageCircle, PlayCircle, Bell, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fontStack = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

const TOPIC_META = {
  "meta-ads": { accent: "bg-blue-500/15 text-blue-300 border-blue-500/30", label: "Meta Ads" },
  "seo": { accent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", label: "SEO" },
  "shopify": { accent: "bg-green-500/15 text-green-300 border-green-500/30", label: "Shopify" },
  "wordpress": { accent: "bg-sky-500/15 text-sky-300 border-sky-500/30", label: "WordPress" },
  "google-ads": { accent: "bg-orange-500/15 text-orange-300 border-orange-500/30", label: "Google Ads" },
  "graphic-design": { accent: "bg-[#FF0066]/15 text-[#FF6699] border-[#FF0066]/30", label: "Graphic Design" },
  "video-editing": { accent: "bg-purple-500/15 text-purple-300 border-purple-500/30", label: "Video Editing" },
  "other": { accent: "bg-white/5 text-white/80 border-white/15", label: "Special" },
};

export default function WebinarsList() {
  const [webinars, setWebinars] = useState([]);
  useEffect(() => {
    axios.get(`${API}/webinars`).then(({ data }) => setWebinars(data)).catch(() => {});
  }, []);

  const now = Date.now();
  const live = webinars.filter((w) => {
    const t = new Date(w.scheduled_at).getTime();
    return t <= now && now <= t + (w.duration_minutes || 90) * 60 * 1000;
  });
  const upcoming = webinars.filter((w) => new Date(w.scheduled_at).getTime() > now);

  return (
    <div className="bg-[#050505] text-white min-h-screen" style={fontStack} data-testid="webinars-list">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-[#FF0066]/15 rounded-full blur-[160px] pointer-events-none" />
        <div className="absolute top-1/2 -left-32 w-[400px] h-[400px] bg-[#FF0066]/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF0066]/10 border border-[#FF0066]/30 text-[#FF0066] text-xs font-medium mb-5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF0066] opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF0066]" />
              </span>
              FREE Live Classes · Every Week
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]" style={displayStack}>
              Learn the exact <span className="text-[#FF0066]">playbooks</span> top marketers use
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl">
              90-minute high-impact live classes on Meta Ads, Google Ads, SEO, Shopify, WordPress and more — taught live by Kamlesh Sir. 100% free, zero fluff, all practical.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#upcoming" className="inline-flex items-center justify-center bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-6 py-3.5 transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.5)]">
                See Upcoming <ArrowRight className="w-4 h-4 ml-2" />
              </a>
              <a href="https://wa.me/919131341638?text=Hi%2C%20webinar%20alerts%20chahiye." target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-medium rounded-full px-6 py-3.5 transition">
                <Bell className="w-4 h-4" /> Get WhatsApp Alerts
              </a>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs max-w-2xl">
              {[
                { icon: Radio, label: "Live + Interactive" },
                { icon: PlayCircle, label: "Recording included" },
                { icon: Users, label: "1000+ joined" },
                { icon: ShieldCheck, label: "No spam, no upsell" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-2 text-white/80">
                  <b.icon className="w-3.5 h-3.5 text-[#FF0066] shrink-0" /> <span className="truncate">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LIVE NOW */}
      {live.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
            <div className="text-xs uppercase tracking-[0.18em] text-red-400 font-bold">Live right now</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {live.map((w) => <WebinarCard key={w.id} w={w} live />)}
          </div>
        </section>
      )}

      {/* UPCOMING */}
      <section id="upcoming" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-semibold mb-2">Upcoming live classes</div>
            <h2 className="text-2xl md:text-3xl font-bold text-white" style={displayStack}>Reserve your seat — limited spots</h2>
          </div>
          <div className="text-xs text-white/50">{upcoming.length} {upcoming.length === 1 ? "class" : "classes"} scheduled</div>
        </div>

        {upcoming.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-[#FF0066]/10 mx-auto flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-[#FF0066]" />
            </div>
            <div className="text-xl font-semibold text-white" style={displayStack}>No upcoming classes right now</div>
            <p className="text-sm text-white/60 mt-2 max-w-md mx-auto">Hum naye topics par regular live classes conduct karte hain. WhatsApp pe alerts paayein — jaisi hi koi class schedule ho, aapko sabse pehle pata chal jayega.</p>
            <a href="https://wa.me/919131341638?text=Hi%2C%20live%20class%20alerts%20chahiye." target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-medium rounded-full px-5 py-2.5 text-sm transition">
              <MessageCircle className="w-4 h-4" /> Get WhatsApp Alerts
            </a>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {upcoming.map((w) => <WebinarCard key={w.id} w={w} />)}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0a0a0a] border-y border-white/5 py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-3">How it works</div>
            <h2 className="text-2xl md:text-3xl font-bold text-white" style={displayStack}>Join a live class in 3 steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Pick a topic", d: "Choose a live class that matches your goal — SEO, Ads, WordPress and more." },
              { n: "02", t: "Reserve seat", d: "Click reserve, fill basic details. Get instant Zoom link + reminders on WhatsApp & email." },
              { n: "03", t: "Show up & learn", d: "Join live, ask questions in real-time, get recording + bonus notes after class." },
            ].map((s) => (
              <div key={s.n} className="bg-[#121212] border border-white/10 rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#FF0066] text-white font-bold mx-auto flex items-center justify-center mb-4 shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]" style={displayStack}>{s.n}</div>
                <div className="text-lg font-semibold text-white mb-2" style={displayStack}>{s.t}</div>
                <p className="text-sm text-white/60 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function WebinarCard({ w, live }) {
  const meta = TOPIC_META[w.topic] || TOPIC_META.other;
  const dt = new Date(w.scheduled_at);
  const regs = w.registration_count || 0;
  const cap = w.max_capacity || 500;
  const pct = Math.min(100, Math.round((regs / cap) * 100));
  return (
    <Link to={`/webinar/${w.slug}`} className="group bg-[#121212] border border-white/10 hover:border-[#FF0066]/40 rounded-2xl overflow-hidden block transition" data-testid={`webinar-card-${w.slug}`}>
      <div className="relative h-44 bg-gradient-to-br from-[#1a1a1a] to-[#050505] overflow-hidden">
        {w.hero_image ? (
          <img src={w.hero_image} alt={w.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-90 transition-all duration-700" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <PlayCircle className="w-14 h-14 text-[#FF0066]/40" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold border backdrop-blur ${live ? "bg-red-500/20 text-red-300 border-red-500/40" : meta.accent}`}>
            {live && <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />}
            {live ? "Live now" : meta.label}
          </span>
        </div>
        {w.is_paid ? (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-amber-500 text-black text-[10px] font-bold">₹{w.price}</div>
        ) : (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-emerald-500 text-black text-[10px] font-bold">FREE</div>
        )}
      </div>
      <div className="p-5">
        <div className="text-base md:text-lg font-semibold text-white line-clamp-2 group-hover:text-[#FF0066] transition" style={displayStack}>{w.title}</div>
        {w.subtitle && <div className="text-xs text-white/60 mt-1.5 line-clamp-2 leading-relaxed">{w.subtitle}</div>}
        <div className="mt-4 flex items-center justify-between text-xs text-white/50">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#FF0066]" /> {dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · {dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {w.duration_minutes}m</span>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-white/60 mb-1.5">
            <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {regs} registered</span>
            <span className={cap - regs < 20 ? "text-amber-400 font-semibold" : ""}>{cap - regs} seats left</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-[#FF0066] rounded-full" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF0066] group-hover:gap-2.5 transition-all">
          {live ? "Join Live" : "Reserve Seat"} <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}
